import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useGetAvailableUniqueProductsWithSellers } from '~/api/product';
import { UniqueProductsCard } from '~/components/cards/UniqueProductsCard';
import type {
  AllUniqueProductsQuery,
  PlatformProduct,
  UniqueProductFiltersInput,
  UniqueProductWithSellers,
} from '~/graphql/graphql';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { ProductFilter, type ProductFilters } from './ProductFilter';
import { useState } from 'react';

export function ProductsView() {
  const [filters, setFilters] = useState<UniqueProductFiltersInput>({});

  const { t } = useTranslation('product');

  const navigate = useNavigate();
  const returnPath = useLocation().pathname;

  const handleFiltering = (filters: ProductFilters) => {
    const updatedFilters: UniqueProductFiltersInput = {
      and: [],
    };

    if (filters.species) {
      updatedFilters.and!.push({
        species: { eq: filters.species },
      });
    }

    if (filters.grade) {
      updatedFilters.and!.push({
        grade: { eq: filters.grade },
      });
    }

    if (filters.dimension) {
      const [thickness, width] = filters.dimension.split('X');
      updatedFilters.and!.push({
        nominalThicknessInches: { eq: parseFloat(thickness) },
        nominalWidthInches: { eq: parseFloat(width) },
      });
    }

    if (filters.location) {
      updatedFilters.and!.push({
        locationId: { eq: filters.location },
      });
    }

    if (filters.stockLevel) {
      switch (filters.stockLevel) {
        case 'low':
          updatedFilters.and!.push({
            stockLevel: { lt: 500 },
          });
          break;
        case 'medium':
          updatedFilters.and!.push({
            stockLevel: { gte: 500, lte: 1500 },
          });
          break;
        case 'high':
          updatedFilters.and!.push({
            stockLevel: { gt: 1500 },
          });
          break;
      }
    }

    if (filters.searchTerm) {
      updatedFilters.and!.push({
        or: [
          { name: { contains: filters.searchTerm } },
          { species: { contains: filters.searchTerm } },
          { grade: { contains: filters.searchTerm } },
        ],
      });
    }
    setFilters(updatedFilters);
  };

  const {
    data: products,
    isLoading,
    hasMoreData,
    sentinelRef,
  } = usePaginatedQuery<
    AllUniqueProductsQuery,
    UniqueProductWithSellers,
    UniqueProductFiltersInput
  >({
    fetchHook: useGetAvailableUniqueProductsWithSellers,
    select: (data) => data?.findAvailableUniqueProductsWithSellers,
    options: { filters },
  });

  return (
    <div className="space-y-4">
      <ProductFilter onChange={handleFiltering} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
        {products.map((product, idx: number) => (
          <UniqueProductsCard
            key={idx}
            listingsCount={product.listingsCount}
            onClick={() =>
              navigate(
                `/order-builder/view?platformProductId=${product!.platformProduct.id}&returnPath=${returnPath}`
              )
            }
            platformProduct={product.platformProduct as PlatformProduct}
            sellersCount={product.sellersCount}
            totalStock={product.totalStock.value}
            unit={product.totalStock.unit}
            priceMin={product.priceMin}
            priceMax={product.priceMax}
            leadTimeMin={product.leadTimeMin}
            leadTimeMax={product.leadTimeMax}
            lengths={
              (product.lengths ?? []).filter((length) => length != null) || []
            }
          />
        ))}
      </div>
      <div
        style={{ height: '1px', background: 'transparent' }}
        ref={sentinelRef}
      ></div>
      {isLoading ? <div>{t('product.loading')}</div> : null}
      {hasMoreData === false && !isLoading && products.length > 0 ? (
        <p className="flex w-full justify-center">{t('product.noMoreData')}</p>
      ) : null}
      {(!products || products?.length === 0) && !isLoading ? (
        <p data-testid="no-products" className="flex w-full justify-center">
          {t('product.no_products')}
        </p>
      ) : null}
    </div>
  );
}
